import React, {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useState,
} from "react"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import Button from "@fuse/sharedComponents/buttons/SolidBtn"

interface AlertConfig {
  image?: string | JSX.Element
  imageText?: string
  title?: string
  description: string | React.ReactNode
  okButtonTitle?: string
  okButtonPress?: () => void
  cancelButtonTitle?: string
  cancelButtonPress?: () => void
  okButtonClassname?: string
  okButtonTextClassname?: string
  cancelButtonClassname?: string
  cancelButtonTextClassname?: string
}

interface AlertContextType {
  showImageAlert: (config: AlertConfig) => void
  hideImageAlert: () => void
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

export const useImageModal = (): AlertContextType => {
  const context = useContext(AlertContext)
  if (!context) {
    throw new Error("useImageModal must be used within an GlobalImageModal")
  }
  return context
}

interface GlobalImageModalProps {
  children: ReactNode
}

export const GlobalImageModal: React.FC<GlobalImageModalProps> = ({
  children,
}): ReactElement => {
  const [alertVisible, setAlertVisible] = useState(false)
  const [alertConfig, setAlertConfig] = useState<AlertConfig>({
    title: "",
    description: "",
  })

  const showImageAlert = (config: AlertConfig) => {
    setAlertConfig(config)
    setAlertVisible(true)
  }

  const hideImageAlert = () => {
    setAlertVisible(false) // Hide the modal
  }

  const hideAlert = () => {
    setAlertVisible(false)
  }

  const parseAndHighlight = (text: string) => {
    // Use a regex to find <hl>...</hl> tags and split the string accordingly
    const regex = /<hl>(.*?)<\/hl>/g

    // Split the text into parts and map through them
    const parts = []
    let match
    let lastIndex = 0

    while ((match = regex.exec(text)) !== null) {
      // Add the text before the matched <hl>...</hl> section
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index))
      }
      // Add the highlighted part
      parts.push(
        <span key={match.index} className="text-white font-medium">
          {match[1]}
        </span>,
      )
      lastIndex = regex.lastIndex
    }

    // Add the remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts
  }

  return (
    <AlertContext.Provider value={{ showImageAlert, hideImageAlert }}>
      {children}
      {alertVisible && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <BlurWrapper
            overlayOpacityDefault="20"
            className="rounded-2xl text-center max-w-sm md:max-w-xl w-full flex flex-col"
          >
            <div className="flex max-w-lg md:px-8 items-center justify-center self-center">
              <div className="px-6 py-10">
                {alertConfig.image &&
                  (typeof alertConfig.image !== "string" ? (
                    alertConfig.image
                  ) : (
                    <img
                      src={alertConfig.image}
                      alt="Alert"
                      className="mb-4 mx-auto w-[128px]"
                    />
                  ))}
                {alertConfig.imageText && (
                  <p className="text-7xl">{alertConfig.imageText}</p>
                )}
                <p className="text-3xl mb-3 mt-6 text-white">
                  {alertConfig.title}
                </p>
                {typeof alertConfig.description === "string" ? (
                  <p
                    className="mb-4 text-white/50 font-thin"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {parseAndHighlight(alertConfig.description)}
                  </p>
                ) : (
                  alertConfig.description
                )}
                <div
                  className={`flex ${
                    alertConfig.okButtonTitle && alertConfig.cancelButtonTitle
                      ? "justify-around md:flex-row flex-col gap-2"
                      : "justify-center flex-col"
                  } mt-8`}
                >
                  {alertConfig.okButtonTitle && (
                    <Button
                      size="lg"
                      className={`${alertConfig.okButtonTitle && alertConfig.cancelButtonTitle ? "md:max-w-48" : "md:max-w-64"} w-full min-w-48 ${alertConfig.cancelButtonTitle ? "" : "mx-auto"} ${alertConfig?.okButtonClassname || ""}`}
                      onClick={() => {
                        alertConfig.okButtonPress && alertConfig.okButtonPress()
                        // hideAlert();
                      }}
                    >
                      <p
                        className={`font-thin ${alertConfig?.okButtonTextClassname || ""}`}
                      >
                        {alertConfig.okButtonTitle}
                      </p>
                    </Button>
                  )}

                  {alertConfig.cancelButtonTitle && (
                    <Button
                      size="lg"
                      variant="outline"
                      className={`${alertConfig.okButtonTitle && alertConfig.cancelButtonTitle ? "md:max-w-48" : "md:max-w-64"} w-full min-w-48 ${alertConfig.cancelButtonTitle ? "" : "mx-auto"} ${alertConfig?.cancelButtonClassname || ""}`}
                      onClick={() => {
                        alertConfig.cancelButtonPress &&
                          alertConfig.cancelButtonPress()
                        hideAlert()
                      }}
                    >
                      <p
                        className={`font-thin ${alertConfig?.cancelButtonTextClassname || ""}`}
                      >
                        {alertConfig.cancelButtonTitle}
                      </p>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </BlurWrapper>
        </div>
      )}
    </AlertContext.Provider>
  )
}
